import { ImageSearchOutlined, MobileScreenShare } from '@mui/icons-material';
import {
  Box,
  Chip,
  Card as MUICard,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { RawUser } from '@prismamedia/one-user';
import clsx from 'clsx';
import { format as formatDate } from 'date-fns';
import fr from 'date-fns/locale/fr';
import React, { FC, ReactNode, useMemo, useState } from 'react';
import { LockButton } from '../ConcurrentialAccess';
import { ImageLoader } from '../ImageLoader';
import { useMenu } from '../Menu';
import {
  CREATED_AT,
  EMPTY_TITLE,
  END_EDITION_TEXT,
  PUSH_LABEL,
  UPDATED_AT,
} from './constants';
import { useStyles } from './styles';

export enum CardSize {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
}

interface CardItemProps {
  ActionsIcons?: ReactNode;
  ActionsMenu?: ReactNode;
  Icons?: ReactNode;
  children?: ReactNode;
  createdAt?: string | null;
  format?: string;
  id: string;
  media: string | null;
  onCardClick?: (id: string) => () => void;
  lockedBy?: Partial<Pick<RawUser, 'name' | 'avatarUrl'>> | null;
  isLocked?: boolean;
  updateLocker?: (
    id: string,
    lockerId: string | null,
    noCache?: boolean,
  ) => void;
  size?: CardSize;
  status?: string;
  title: string | null;
  topTitle?: string;
  updatedAt?: string | null;
  brandKey?: string;
  eventTitle?: string;
  categoryTitle?: string;
  dataTestId?: string;
  isPaidArticle?: boolean;
  statusTitle?: string;
  hasPush?: boolean;
  pushCount?: number;
  titleOverflow?: boolean;
}

export const CardItem: FC<CardItemProps> = ({
  ActionsIcons,
  ActionsMenu,
  Icons,
  children,
  createdAt,
  id,
  media,
  onCardClick,
  lockedBy,
  isLocked,
  updateLocker,
  size = CardSize.LARGE,
  title,
  topTitle,
  updatedAt,
  brandKey,
  eventTitle,
  categoryTitle,
  dataTestId,
  isPaidArticle,
  statusTitle,
  hasPush,
  pushCount,
  titleOverflow,
}) => {
  const { openMenu, closeMenu } = useMenu();
  const [isCardOver, setIsCardOver] = useState(false);
  const classes = useStyles({ isCardOver, size });

  const topTitleDisplayed = useMemo(() => {
    if (topTitle) {
      return topTitle;
    }

    let createdAtText = createdAt
      ? `${CREATED_AT} ${formatDate(new Date(createdAt), 'dd/MM/yyyy à HH:mm', {
          locale: fr,
        })}`
      : null;

    const updatedAtText = updatedAt
      ? `${UPDATED_AT} ${formatDate(new Date(updatedAt), 'dd/MM/yyyy à HH:mm', {
          locale: fr,
        })}`
      : null;

    const isTopTitleEmpty = !createdAtText?.length && !updatedAtText?.length;

    return (
      <>
        {createdAtText ? (createdAtText += updatedAtText ? ' - ' : '') : ''}
        {updatedAtText ?? ''}
        {!isTopTitleEmpty ? '.' : ''}
      </>
    );
  }, [createdAt, updatedAt, topTitle]);

  return (
    <MUICard
      className={classes.wrapper}
      onClick={onCardClick?.(id)}
      onMouseLeave={() => {
        setIsCardOver(false);
      }}
      onMouseOver={() => {
        setIsCardOver(true);
      }}
      variant="outlined"
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <div className={classes.innerWrapper}>
        {/* Card media thumbnail */}
        <div className={classes.wrapperLeftSide}>
          <Typography className={classes.brandkey}>{brandKey}</Typography>
          <ImageLoader
            className={classes.media}
            image={<ImageSearchOutlined className={classes.imageFallback} />}
            loaderSize={35}
            src={media}
            timeout={3000}
          />
        </div>

        {/* Card Title & description */}
        <div className={classes.wrapperCenter}>
          <div className={classes.titleWrapper}>
            {isPaidArticle && <div className={classes.paidArticle}>★</div>}
            <Typography className={classes.topTitle} variant="body2">
              {topTitleDisplayed}
              {Boolean(hasPush) && ` | `}
            </Typography>
            {Boolean(hasPush) && (
              <Tooltip title={`${PUSH_LABEL} ${pushCount || 0}`}>
                <MobileScreenShare fontSize="small" sx={{ color: grey[500] }} />
              </Tooltip>
            )}
          </div>

          <Typography
            variant="h5"
            component="h2"
            className={clsx(!titleOverflow && classes.mainTitle)}
          >
            {title || <em className={classes.empty}>{EMPTY_TITLE}</em>}
          </Typography>

          {statusTitle && (
            <Chip
              className={classes.status}
              label={statusTitle}
              variant="outlined"
              size="small"
            />
          )}

          {/* Action Icons/Menu (top/right) */}
          <div className={classes.wrapperActionsIcons}>
            {ActionsIcons} {children || ActionsMenu}
          </div>

          {/* Event and Category Title */}
          <Box>
            {eventTitle && (
              <Typography className={classes.eventTitle} variant="caption">
                {eventTitle}
              </Typography>
            )}
            <br />
            {categoryTitle && (
              <Typography className={classes.categoryTitle} variant="caption">
                {categoryTitle}
              </Typography>
            )}
          </Box>
        </div>

        {/* Card Icons */}
        <div className={classes.wrapperRightSide}>
          {Icons}

          {lockedBy && (
            <LockButton
              className={classes.lockButton}
              disableLockForV0
              lockedBy={lockedBy}
              onClick={(e) => {
                e.preventDefault();
                if (isLocked) {
                  openMenu(
                    e.currentTarget,
                    <MenuItem
                      onClick={() => {
                        updateLocker?.(id, null);
                        closeMenu();
                      }}
                    >
                      {END_EDITION_TEXT}
                    </MenuItem>,
                  );
                }
              }}
              size="small"
            />
          )}
        </div>
      </div>
    </MUICard>
  );
};
